import { useCallback, useEffect, useState } from 'react'
import Bugsnag from '@bugsnag/js'
import { useStripe } from '@stripe/react-stripe-js'
import { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js'
import { Offer } from '@/services/PifService'
import { PaymentMethod } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { OrderCompleteArgs } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'

type PaymentIntentType = {
  id?: string
  payment_method?: string | PaymentMethod | null
  status?: string
}

export const useAppleGooglePay = ({
  onComplete,
  offer,
  skip,
  onError,
  clientSecret,
}: {
  isGuest?: boolean
  onComplete: (props: OrderCompleteArgs) => void
  onError?: (error: string) => void
  offer: Offer
  skip?: boolean
  clientSecret: string
}) => {
  const stripe = useStripe()
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [errorMessageWithWallet, setErrorMessageWithWallet] = useState<string | null>(null)
  const [isSubmittingWithWallet, setIsSubmittingWithWallet] = useState<boolean>(false)
  const track = useSafeTrack()

  const setError = useCallback(
    (error: string) => {
      setErrorMessageWithWallet(error)
      onError?.(error)
    },
    [onError],
  )

  useEffect(() => {
    if (skip) return
    if (stripe && offer && !paymentRequest) {
      const pr: PaymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: offer.currency.toLowerCase(),
        total: {
          label: offer.name,
          amount: offer.price,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      // Check the availability of the Payment Request API.
      pr.canMakePayment()
        .then((result) => {
          if (result) {
            setPaymentRequest(pr)
          }
        })
        .catch((e) => {
          Bugsnag.notify(Object.assign(e, new Error('Could not create wallet payment request')))
          throw e
        })
    } else if (stripe && offer && paymentRequest) {
      paymentRequest.update({
        currency: offer.currency.toLowerCase(),
        total: {
          label: offer.name,
          amount: offer.price,
        },
      })
    }
  }, [stripe, offer, skip, paymentRequest])

  useEffect(() => {
    const canProceedWithPayment = stripe && paymentRequest && onComplete
    if (skip || !canProceedWithPayment) return

    const sendBugsnagError = (msg: string) => {
      Bugsnag.notify(
        Object.assign(new Error(msg), {
          offerId: offer.id,
          region: offer.region,
        }),
      )
    }

    const handlePaymentSuccess = (paymentIntent: PaymentIntentType) => {
      onComplete({
        orderId: paymentIntent?.id ?? 'unknown',
        paymentMethod: paymentIntent?.payment_method as string,
      })
      setIsSubmittingWithWallet(false)
      track('completed wallet paymentv2', { paymentMethod: paymentIntent?.payment_method })
    }

    const processPaymentIntent = async (ev: PaymentRequestPaymentMethodEvent) => {
      track('started wallet paymentv2')
      setIsSubmittingWithWallet(true)
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      )

      if (confirmError) {
        handlePaymentFailure(ev)
      } else if (paymentIntent) {
        handleConfirmedPayment(ev, paymentIntent)
      }
    }

    const handlePaymentFailure = (ev: PaymentRequestPaymentMethodEvent) => {
      setIsSubmittingWithWallet(false)
      sendBugsnagError('wallet payment confirmation error')
      setError('Could not complete payment, please try again')
      ev.complete('fail')
    }

    const handleConfirmedPayment = async (ev: PaymentRequestPaymentMethodEvent, paymentIntent: PaymentIntentType) => {
      ev.complete('success')
      if (paymentIntent?.status === 'requires_action') {
        const { error } = await stripe.confirmCardPayment(clientSecret)
        if (error) {
          setIsSubmittingWithWallet(false)
          return // Exit early if there's an error
        }
      }
      handlePaymentSuccess(paymentIntent)
    }

    paymentRequest?.on('paymentmethod', async (ev) => {
      try {
        processPaymentIntent(ev)
      } catch (e) {
        sendBugsnagError('general error confirming wallet payment')
        throw e
      }
    })
  }, [track, onComplete, clientSecret, stripe, paymentRequest, skip, setError, offer.id, offer.region])

  return { isSubmittingWithWallet, paymentRequest, errorMessageWithWallet }
}
